import {
  BULK_MODES,
  type BulkMode,
  type VesselFileSettings,
  validateVesselFileSettings,
} from '@felix/domain';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconFolderDown, IconLock } from '@tabler/icons-react';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { errorCode } from '../personnel/source.js';
import classes from './Documents.module.css';
import { formatFileSize } from './library.js';
import type { OfficeDocumentActions } from './source.js';
import type { DistributionVessel, UsbExportList, UsbExportProgress } from './types.js';

const MB_PER_GB = 1024;

/** Formdaki değerler kullanıcının biriminde: kota ve aylık sınır GB, eşik MB. */
interface SettingsForm {
  quotaGb: number | string;
  thresholdMb: number | string;
  mode: BulkMode;
  start: string;
  end: string;
  capGb: number | string;
  rateKbps: number | string;
}

const toForm = (value: VesselFileSettings): SettingsForm => ({
  quotaGb: Math.round((value.filesQuotaMb / MB_PER_GB) * 10) / 10,
  thresholdMb: value.bulkThresholdMb,
  mode: value.bulkMode,
  start: value.bulkWindowStart,
  end: value.bulkWindowEnd,
  capGb: Math.round((value.monthlyBulkCapMb / MB_PER_GB) * 10) / 10,
  rateKbps: value.maxRateKbps,
});

const number = (value: number | string) => (typeof value === 'number' ? value : Number(value));

function fromForm(form: SettingsForm): VesselFileSettings {
  return {
    filesQuotaMb: Math.round(number(form.quotaGb) * MB_PER_GB),
    bulkThresholdMb: Math.round(number(form.thresholdMb)),
    bulkMode: form.mode,
    bulkWindowStart: form.start,
    bulkWindowEnd: form.end,
    monthlyBulkCapMb: Math.round(number(form.capGb) * MB_PER_GB),
    maxRateKbps: Math.round(number(form.rateKbps)),
  };
}

/**
 * Gemi bazlı indirme ayarı (ADR 0028 #31 madde 5, #40): kota, hemen inen dosya sınırı, toplu
 * indirme kipi ve gemi saatiyle penceresi, aylık sınır ve hız. Domain kuralıyla doğrulanır
 * (`validateVesselFileSettings`); yazma yetkisini (`settings`) sunucu denetler.
 */
export function VesselFileSettingsForm({
  vesselName,
  value,
  readOnly = false,
  onCancel,
  onSave,
}: {
  vesselName: string;
  value: VesselFileSettings;
  /** Yazma yetkisi (`settings`) yok: alanlar pasif, Save yok (inceleme WF-4). */
  readOnly?: boolean;
  onCancel: () => void;
  onSave: (settings: VesselFileSettings) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.vesselFiles.${key}`, options ?? {});
  const [form, setForm] = useState<SettingsForm>(() => toForm(value));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof SettingsForm>(key: K, next: SettingsForm[K]) =>
    setForm((current) => ({ ...current, [key]: next }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (readOnly) return;
    const settings = fromForm(form);
    try {
      validateVesselFileSettings(settings);
    } catch {
      setError(k('invalid'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSave(settings);
    } catch (failure) {
      setError(
        t(`documents.errors.${errorCode(failure)}`, {
          defaultValue: t('documents.errors.GENERIC'),
        }),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={(event) => void submit(event)} noValidate aria-label={k('tabs.settings')}>
      <Stack gap="md">
        <span className={classes.srOnly}>{vesselName}</span>
        {error ? (
          <Alert color="red" icon={<IconAlertTriangle size={16} />} role="alert">
            {error}
          </Alert>
        ) : null}
        {readOnly ? (
          <Alert color="info" icon={<IconLock size={16} />}>
            {k('readOnly')}
          </Alert>
        ) : null}
        <fieldset disabled={readOnly} className={classes.plainFieldset}>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
              <NumberInput
                label={k('quota')}
                min={1}
                max={1024}
                decimalScale={1}
                value={form.quotaGb}
                onChange={(next) => set('quotaGb', next)}
                required
              />
              <NumberInput
                label={k('threshold')}
                description={k('thresholdHint')}
                min={1}
                max={1024}
                allowDecimal={false}
                value={form.thresholdMb}
                onChange={(next) => set('thresholdMb', next)}
                required
              />
            </SimpleGrid>
            <Select
              label={k('mode')}
              data={BULK_MODES.map((mode) => ({ value: mode, label: k(`modes.${mode}`) }))}
              value={form.mode}
              allowDeselect={false}
              onChange={(next) => next && set('mode', next as BulkMode)}
            />
            {form.mode === 'window' ? (
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
                <TimeInput
                  label={k('windowStart')}
                  description={k('windowHint')}
                  value={form.start}
                  onChange={(next) => set('start', next)}
                />
                <TimeInput
                  label={k('windowEnd')}
                  description={k('windowHint')}
                  value={form.end}
                  onChange={(next) => set('end', next)}
                />
              </SimpleGrid>
            ) : null}
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing="md">
              <NumberInput
                label={k('cap')}
                description={k('capHint')}
                min={0}
                max={1024}
                decimalScale={1}
                value={form.capGb}
                onChange={(next) => set('capGb', next)}
              />
              <NumberInput
                label={k('rate')}
                description={k('rateHint')}
                min={0}
                max={10_000_000}
                allowDecimal={false}
                value={form.rateKbps}
                onChange={(next) => set('rateKbps', next)}
              />
            </SimpleGrid>
          </Stack>
        </fieldset>
        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>
            {k('cancel')}
          </Button>
          {readOnly ? null : (
            <Button type="submit" loading={busy}>
              {k('save')}
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  );
}

/**
 * "Export for USB" (ADR 0028 #33 b): geminin henüz doğrulamadığı dosyalar seçilen klasöre hash
 * adlı düzende yazılır; gemide "Import from folder" ile içe aktarılır. File System Access API
 * gerekir (Chrome, Edge); diğer tarayıcıda düğme açıklamayla pasiftir.
 */
export function UsbExportForm({
  list,
  supported,
  progress,
  onCancel,
  onExport,
}: {
  list: UsbExportList;
  supported: boolean;
  progress: UsbExportProgress | null;
  onCancel: () => void;
  onExport: () => void;
}) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.vesselFiles.${key}`, options ?? {});
  const unique = new Set(list.files.map((f) => f.sha256)).size;
  const running = progress !== null && progress.done < progress.total;
  return (
    <Stack gap="md">
      <Text size="sm">{k('exportBody', { vessel: list.vessel.name })}</Text>
      {unique === 0 ? (
        <Text size="sm" c="dimmed">
          {k('exportNone')}
        </Text>
      ) : (
        <Text size="sm" fw={600}>
          {k('exportCount', { count: unique })} · {formatFileSize(list.totalBytes, i18n.language)}
        </Text>
      )}
      {progress ? (
        <div className={classes.drawerProgress}>
          <Text size="xs" role="status">
            {progress.done >= progress.total
              ? k('exportDone')
              : k('exportProgress', { done: progress.done, total: progress.total })}
          </Text>
          <Progress
            value={progress.total > 0 ? (progress.done / progress.total) * 100 : 100}
            size="sm"
            aria-label={k('tabs.export')}
          />
        </div>
      ) : null}
      {supported ? null : (
        <Alert color="gray" icon={<IconAlertTriangle size={16} />}>
          {k('unsupported')}
        </Alert>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          {k('cancel')}
        </Button>
        <Button
          leftSection={<IconFolderDown size={16} />}
          disabled={!supported || unique === 0}
          loading={running}
          onClick={onExport}
        >
          {k('exportStart')}
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * Pencerenin bir sekmesinin okuma durumu (inceleme M8a3-R26): yükleniyor kendi metniyle, hata
 * Retry ile; okuma başarısızsa sonsuz "yükleniyor" kalmaz.
 */
export function VesselFilesPanel({
  state,
  onRetry,
  children,
}: {
  state: 'loading' | 'error' | 'ready';
  onRetry: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (state === 'ready') return <>{children}</>;
  if (state === 'error') {
    return (
      <EmptyState
        tone="danger"
        title={t('documents.vesselFiles.loadError')}
        action={
          <Button size="xs" variant="light" onClick={onRetry}>
            {t('documents.screen.retry')}
          </Button>
        }
      />
    );
  }
  return (
    <Text size="sm" c="dimmed" aria-busy="true">
      {t('documents.vesselFiles.loading')}
    </Text>
  );
}

/** Dağıtım panosunda geminin dosyaları: indirme ayarı ve USB için dışa aktarma. */
export function VesselFilesDialog({
  vessel,
  office,
  canEdit,
  onClose,
  onSaved,
}: {
  vessel: DistributionVessel | null;
  office: OfficeDocumentActions;
  /** Ayarı yazma yetkisi (`settings`); yoksa form salt okunur (inceleme WF-4). */
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.vesselFiles.${key}`, options ?? {});
  const [tab, setTab] = useState<string | null>('settings');
  const [settings, setSettings] = useState<{
    rowVersion: number | null;
    settings: VesselFileSettings;
  } | null>(null);
  const [list, setList] = useState<UsbExportList | null>(null);
  const [progress, setProgress] = useState<UsbExportProgress | null>(null);
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const vesselId = vessel?.id ?? null;
  // Sekme başına okuma hatası ve yeniden deneme (inceleme M8a3-R26)
  const [failed, setFailed] = useState<{ settings: boolean; list: boolean }>({
    settings: false,
    list: false,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!vesselId) return;
    void attempt;
    setSettings(null);
    setList(null);
    setProgress(null);
    setMessage(null);
    let live = true;
    setFailed({ settings: false, list: false });
    const fail = (part: 'settings' | 'list') => (failure: unknown) => {
      if (!live) return;
      setFailed((current) => ({ ...current, [part]: true }));
      setMessage({
        tone: 'danger',
        text: t(`documents.errors.${errorCode(failure)}`, {
          defaultValue: t('documents.errors.GENERIC'),
        }),
      });
    };
    void office
      .fileSettings(vesselId)
      .then((value) => live && setSettings(value), fail('settings'));
    void office.exportList(vesselId).then((value) => live && setList(value), fail('list'));
    return () => {
      live = false;
    };
  }, [vesselId, office, t, attempt]);

  // Başka gemiye geçilince ilk sekme
  useEffect(() => {
    if (vesselId) setTab('settings');
  }, [vesselId]);

  const retry = () => setAttempt((value) => value + 1);

  const exportFiles = async () => {
    if (!list || !office.exportToFolder) return;
    setMessage(null);
    try {
      await office.exportToFolder(list, setProgress);
    } catch (failure) {
      setProgress(null);
      const code = errorCode(failure);
      if (code !== 'CANCELLED') {
        setMessage({
          tone: 'danger',
          text: t(`documents.errors.${code}`, { defaultValue: t('documents.errors.GENERIC') }),
        });
      }
    }
  };

  return (
    <Modal
      opened={vessel !== null}
      onClose={onClose}
      title={vessel ? k('title', { vessel: vessel.name }) : ''}
      closeButtonProps={{ 'aria-label': t('documents.screen.dismiss') }}
      size="lg"
      centered
    >
      {message ? (
        <Alert
          color={message.tone === 'danger' ? 'red' : 'green'}
          icon={
            message.tone === 'danger' ? (
              <IconAlertTriangle size={16} />
            ) : (
              <IconCircleCheck size={16} />
            )
          }
          mb="md"
          role={message.tone === 'danger' ? 'alert' : 'status'}
        >
          {message.text}
        </Alert>
      ) : null}
      <Tabs value={tab} onChange={setTab}>
        <Tabs.List mb="md">
          <Tabs.Tab value="settings">{k('tabs.settings')}</Tabs.Tab>
          <Tabs.Tab value="export">{k('tabs.export')}</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="settings">
          <VesselFilesPanel
            state={settings && vessel ? 'ready' : failed.settings ? 'error' : 'loading'}
            onRetry={retry}
          >
            {settings && vessel ? (
              <VesselFileSettingsForm
                key={`${vessel.id}-${settings.rowVersion ?? 0}`}
                vesselName={vessel.name}
                value={settings.settings}
                readOnly={!canEdit}
                onCancel={onClose}
                onSave={async (next) => {
                  const saved = await office.saveFileSettings(vessel.id, settings.rowVersion, next);
                  setSettings(saved);
                  setMessage({ tone: 'success', text: k('saved') });
                  onSaved();
                }}
              />
            ) : null}
          </VesselFilesPanel>
        </Tabs.Panel>
        <Tabs.Panel value="export">
          <VesselFilesPanel
            state={list ? 'ready' : failed.list ? 'error' : 'loading'}
            onRetry={retry}
          >
            {list ? (
              <UsbExportForm
                list={list}
                supported={Boolean(office.exportToFolder)}
                progress={progress}
                onCancel={onClose}
                onExport={() => void exportFiles()}
              />
            ) : null}
          </VesselFilesPanel>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
